import clsx from "clsx";
import React, { useCallback, useMemo } from "react";

import { CompanySiteType } from "../../../../types/company-site";
import { translateWeekday, Weekday } from "../../../../utils/dates";
import { isStoreAlwaysClosed } from "../../../../utils/timetable";
import { Button } from "../../../buttons/button";
import { Icon } from "../../../icon";
import { RichText } from "../../../rich-text";
import { StoreIcon } from "../store-icon";

export interface StoreDetailProps {
  store: Queries.CompanySiteDetailDataFragment;
  labels: {
    "store-locator-store-closed": string;
    "store-locator-store-address": string;
    "store-locator-store-contacts": string;
    "store-locator-store-timetable": string;
    "store-locator-store-directions": string;
    "store-locator-store-fax": string;
    "store-locator-store-phone": string;
    "store-locator-store-email": string;
    // "store-locator-store-contact-cta": string;
    "company-site-types-biesse-venue": string;
    "company-site-types-dealer": string;
    "company-site-types-production-site": string;
  };
  className?: string;
  onClose?: () => void;
}

export function StoreDetail({ store, labels, onClose, className }: StoreDetailProps) {
  const renderOpeningTime = useCallback(
    (from: string | null, to: string | null) => {
      if (!from && !to) return labels["store-locator-store-closed"];
      return [from ?? "00:00", to ?? "00:00"].filter(Boolean).join(" - ");
    },
    [labels]
  );

  const onClickDirections = useCallback(() => {
    if (typeof window !== "undefined") {
      const googleMapsQuery = [
        store.name,
        store.address,
        store.city,
        store.state,
        store.zipCode,
        store.country,
      ]
        .filter(Boolean)
        .join(" ");
      window.open(`https://maps.google.com/?q=${googleMapsQuery}`, "_blank");
    }
  }, [store]);

  const formattedSiteType = useMemo(() => {
    const formattedTypes: Record<CompanySiteType, string> = {
      [CompanySiteType.BiesseVenue]: labels["company-site-types-biesse-venue"],
      [CompanySiteType.ProductionSite]: labels["company-site-types-production-site"],
      [CompanySiteType.Dealer]: labels["company-site-types-dealer"],
    };
    return store.siteType ? formattedTypes[store.siteType as CompanySiteType] : null;
  }, [store.siteType, labels]);

  return (
    <div
      className={clsx(
        "lg:bg-neutral-light rounded-xl flex flex-col gap-2 p-2 lg:p-3 text-sm relative",
        className
      )}
    >
      {onClose && (
        <Icon
          icon="close"
          className="cursor-pointer absolute left-2 top-2 hidden lg:block"
          size={24}
          onClick={onClose}
        />
      )}
      <div className="flex justify-between w-full items-baseline">
        <div>
          <h3 className="text-xl text-primary">{store.name}</h3>
          {formattedSiteType && <p>{formattedSiteType}</p>}
        </div>
        {store.siteType && <StoreIcon type={store.siteType as CompanySiteType} />}
      </div>
      <p>{store.description?.raw && <RichText raw={store.description?.raw} />}</p>

      <div>
        <strong>{labels["store-locator-store-address"]}</strong>
        <p>{store.address}</p>
        <p>
          {[
            store.city, // required field
            [store.zipCode, store.state].filter(Boolean).join(" - "), // optional fields
          ]
            .filter(Boolean)
            .join(", ")}
          &nbsp;
          {store.country}
        </p>

        <div className="pt-2">
          <Button
            size="sm"
            variant="primary-outline"
            className="flex gap-2 items-center font-bold"
            onClick={onClickDirections}
          >
            {labels["store-locator-store-directions"]}
            <Icon icon="directions" className="text-primary" size={28} />
          </Button>
        </div>
      </div>

      {[store.email, store.phone, store.fax].some(Boolean) && (
        <div>
          <strong>{labels["store-locator-store-contacts"]}</strong>
          <p>
            {labels["store-locator-store-email"]}:&nbsp;
            {store.email ? (
              <a className="text-primary hover:underline" href={`mailto:${store.email}`}>
                {store.email}
              </a>
            ) : (
              "-"
            )}
          </p>
          <p>
            {labels["store-locator-store-phone"]}:&nbsp;
            {store.phone ? (
              <a className="text-primary hover:underline" href={`tel:${store.phone}`}>
                {store.phone}
              </a>
            ) : (
              "-"
            )}
          </p>
          <p>
            {labels["store-locator-store-fax"]}:&nbsp;
            {store.fax ? <a href={store.fax}>{store.fax}</a> : "-"}
          </p>

          {/* <div className="flex items-center justify-between gap-4 pt-2">
          <Button size="sm" variant="primary-outline">
            {labels["store-locator-store-contact-cta"]}
          </Button>
        </div> */}
        </div>
      )}

      {!isStoreAlwaysClosed(store) && (
        <div>
          <strong>{labels["store-locator-store-timetable"]}</strong>
          <p className="text-xs pb-1">{store.timezone}</p>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Monday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.mondayOpensAt, store.mondayClosesAt)}</span>
          </div>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Tuesday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.tuesdayOpensAt, store.tuesdayClosesAt)}</span>
          </div>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Wednesday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.wednesdayOpensAt, store.wednesdayClosesAt)}</span>
          </div>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Thursday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.thursdayOpensAt, store.thursdayClosesAt)}</span>
          </div>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Friday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.fridayOpensAt, store.fridayClosesAt)}</span>
          </div>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Saturday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.saturdayOpensAt, store.saturdayClosesAt)}</span>
          </div>
          <div className="flex items-center justify-between w-full">
            <span>{translateWeekday(Weekday.Sunday, store.node_locale)}</span>
            <span>{renderOpeningTime(store.sundayOpensAt, store.sundayClosesAt)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
